import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, Send } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";

const when = (d) => new Date(d).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

function Conversation({ tid, isAdmin, onBack, onSent }) {
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  const load = useCallback(() => api.get(`/threads/${tid}/messages`).then((r) => setData(r.data)), [tid]);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);
  useEffect(() => end.current?.scrollIntoView({ block: "end" }), [data?.messages.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post(`/threads/${tid}/messages`, { body: text });
      setText("");
      await load();
      onSent();
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };

  if (!data) return <div className="p-8 text-mist">Loading…</div>;
  const { thread, messages } = data;
  return (
    <div className="flex h-full flex-col" data-testid="conversation">
      <div className="flex items-center gap-3 border-b border-navy/10 p-4">
        <button onClick={onBack} className="md:hidden" aria-label="Back" data-testid="conversation-back"><ArrowLeft className="h-5 w-5 text-navy" /></button>
        <div className="min-w-0">
          <p className="font-semibold text-navy truncate">{isAdmin ? `${thread.investor_name} · ${thread.investor_email}` : "Sido Holdings"}</p>
          <p className="text-xs text-mist truncate">
            Re: {thread.property_slug ? <Link to={`/opportunities/${thread.property_slug}`} className="underline">{thread.property_title}</Link> : thread.property_title}
          </p>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto space-y-3 p-4 bg-paper/60" data-testid="conversation-messages">
        {messages.map((m) => {
          const mine = isAdmin ? m.sender_role === "admin" : m.sender_role !== "admin";
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] px-4 py-3 text-sm ${mine ? "bg-navy text-paper" : "bg-white border border-navy/10 text-ink"}`} data-testid={`message-${m.id}`}>
                <p className="whitespace-pre-wrap leading-relaxed">{m.body}</p>
                <p className={`mt-1.5 text-[10px] ${mine ? "text-paper/50" : "text-mist"}`}>{m.sender_role === "admin" ? "Sido Holdings" : m.sender_name} · {when(m.created_at)}</p>
              </div>
            </div>
          );
        })}
        <div ref={end} />
      </div>
      <form onSubmit={send} className="flex gap-2 border-t border-navy/10 p-3 bg-white">
        <textarea rows={2} className="field !py-2.5 text-sm resize-none" placeholder="Write a message…" value={text} onChange={(e) => setText(e.target.value)} data-testid="message-input"
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) send(e); }} />
        <button disabled={busy} className="btn-primary !px-4" aria-label="Send" data-testid="message-send"><Send className="h-4 w-4" /></button>
      </form>
    </div>
  );
}

function NewThread({ onCreated, onCancel }) {
  const [text, setText] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/threads", { body: text });
      onCreated(data.thread_id);
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={submit} className="p-6 space-y-4" data-testid="new-thread-form">
      <p className="font-serif text-2xl text-navy">Message Sido Holdings</p>
      <textarea required className="field min-h-[140px]" placeholder="Ask a question about your search, a market, or next steps…" value={text} onChange={(e) => setText(e.target.value)} data-testid="new-thread-input" />
      <div className="flex gap-2">
        <button className="btn-primary" data-testid="new-thread-send">Send Message</button>
        <button type="button" onClick={onCancel} className="btn-ghost text-navy">Cancel</button>
      </div>
    </form>
  );
}

export default function Messages({ isAdmin = false, onChange }) {
  const [threads, setThreads] = useState(null);
  const [active, setActive] = useState(null);
  const [composing, setComposing] = useState(false);
  const flag = isAdmin ? "unread_admin" : "unread_investor";
  const load = useCallback(() => api.get("/threads").then((r) => { setThreads(r.data); onChange?.(r.data); }), [onChange]);
  useEffect(() => { load(); }, [load]);

  const open = (id) => {
    setComposing(false);
    setActive(id);
    setThreads((ts) => ts?.map((t) => (t.id === id ? { ...t, [flag]: false } : t)));
  };
  const showPane = active || composing;

  return (
    <div className="grid md:grid-cols-[320px_1fr] border border-navy/10 bg-white h-[620px]" data-testid="messages-panel">
      <div className={`${showPane ? "hidden md:flex" : "flex"} flex-col border-r border-navy/10 min-h-0`}>
        {!isAdmin && (
          <button onClick={() => { setActive(null); setComposing(true); }} className="btn-primary m-3" data-testid="new-thread-btn"><Plus className="h-4 w-4" /> New Message</button>
        )}
        <div className="flex-1 overflow-y-auto divide-y divide-navy/10" data-testid="thread-list">
          {threads?.length === 0 && <p className="p-6 text-sm text-mist">No conversations yet.{!isAdmin && " Ask a question from any property page or start a new message."}</p>}
          {threads?.map((t) => (
            <button key={t.id} onClick={() => open(t.id)} className={`w-full text-left p-4 transition-colors ${active === t.id ? "bg-paper" : "hover:bg-paper/60"}`} data-testid={`thread-${t.id}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold text-navy text-sm truncate">{isAdmin ? t.investor_name : t.property_title}</span>
                {t[flag] && <span className="h-2 w-2 rounded-full bg-gold shrink-0" data-testid={`thread-unread-${t.id}`} />}
              </div>
              {isAdmin && <p className="text-xs text-navy/70 truncate">{t.property_title}</p>}
              <p className="mt-1 text-xs text-mist truncate">{t.last_message}</p>
              <p className="mt-1 text-[10px] text-mist">{when(t.last_at)}</p>
            </button>
          ))}
        </div>
      </div>
      <div className={`${showPane ? "flex" : "hidden md:flex"} flex-col min-h-0`}>
        {composing ? (
          <NewThread onCancel={() => setComposing(false)} onCreated={(id) => { load(); open(id); }} />
        ) : active ? (
          <Conversation key={active} tid={active} isAdmin={isAdmin} onBack={() => setActive(null)} onSent={load} />
        ) : (
          <div className="m-auto p-8 text-center text-mist text-sm">Select a conversation.</div>
        )}
      </div>
    </div>
  );
}
